import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Mail, Phone, MapPin, Check } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  consultancyServices,
  consultancyProcess,
  consultancyPillars,
} from "@/data/consultancy";
import { useInquiry } from "@/contexts/InquiryContext";

export default function Consultancy() {
  const { openInquiry } = useInquiry();

  useEffect(() => {
    document.title =
      "Consultancy — Kenneth Nathanael Lukman LLC · Independent Property Advisory";
  }, []);

  return (
    <div
      data-testid="page-consultancy"
      className="min-h-screen bg-[#1F2620] text-[#EDE4D3]"
    >
      <Navbar variant="dark" />

      {/* HERO */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/consultancy/hero.jpeg"
            alt="Editorial view of a private London consultancy boardroom at dusk"
            className="w-full h-full object-cover opacity-40"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1F2620] via-[#1F2620]/70 to-[#1F2620]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-4xl"
          >
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#B8865B] mb-6">
              Independent Property Consulting · Kenneth Nathanael Lukman LLC
            </div>
            <h1
              data-testid="consultancy-headline"
              className="font-display text-4xl sm:text-5xl lg:text-7xl font-light leading-[1.05] tracking-tight text-[#F7F3EA]"
            >
              Independent counsel for the{" "}
              <span className="italic text-[#B8865B]">landlords of prime London</span>
              .
            </h1>
            <p className="mt-8 text-base md:text-lg text-[#EDE4D3]/75 leading-relaxed max-w-2xl font-light">
              Kenneth Nathanael Lukman LLC is a boutique property consultancy
              that curates and oversees premium serviced apartments across the
              United Kingdom on behalf of private owners, family offices and
              investors. We fold design, operations and yield strategy into a
              single, calm mandate — always in the owner&apos;s name, always
              with the owner in control.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <button
                type="button"
                onClick={() => openInquiry("__consultancy__")}
                data-testid="consultancy-cta-book"
                className="inline-flex items-center justify-center gap-2 bg-[#B8865B] hover:bg-[#A57548] text-[#1F2620] px-8 py-4 rounded-md font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5 shadow-lg shadow-black/30"
              >
                Book Advisory Call
                <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="#services"
                data-testid="consultancy-cta-services"
                className="inline-flex items-center justify-center bg-transparent border border-[#EDE4D3]/30 hover:border-[#B8865B] hover:text-[#B8865B] text-[#EDE4D3] px-8 py-4 rounded-md font-medium tracking-wide transition-all duration-300"
              >
                See Our Practice
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Positioning strip */}
      <section className="border-y border-[#EDE4D3]/10 bg-[#171C17]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
          {consultancyPillars.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              data-testid={`pillar-${i}`}
            >
              <div className="text-[11px] tracking-[0.2em] uppercase text-[#B8865B] mb-3">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="font-display text-lg text-[#F7F3EA] leading-snug">
                {p.title}
              </div>
              <p className="mt-3 text-sm text-[#EDE4D3]/60 leading-relaxed">
                {p.body}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SERVICES — alternating editorial layout */}
      <section id="services" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#B8865B] mb-5">
              Three lines of practice
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight leading-[1.1] text-[#F7F3EA]">
              Engaged individually, or composed into a single{" "}
              <span className="italic text-[#B8865B]">advisory mandate</span>.
            </h2>
            <div className="mt-6 h-px w-16 bg-[#B8865B]" />
          </motion.div>

          <div className="mt-20 space-y-24 md:space-y-32">
            {consultancyServices.map((s, i) => {
              const imageLeft = i % 2 === 0;
              return (
                <motion.div
                  key={s.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-120px" }}
                  transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                  data-testid={`service-${s.id}`}
                  className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center"
                >
                  <div
                    className={`lg:col-span-6 ${imageLeft ? "" : "lg:order-2"}`}
                  >
                    <div className="relative aspect-[5/4] w-full overflow-hidden rounded-xl border border-[#EDE4D3]/10 bg-[#171C17]">
                      <img
                        src={s.image}
                        alt={s.title}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-6 left-6 bg-[#1F2620]/80 backdrop-blur-md text-[#B8865B] px-4 py-2 rounded-full text-xs tracking-[0.2em]">
                        Service · {s.number}
                      </div>
                    </div>
                  </div>

                  <div className={`lg:col-span-6 ${imageLeft ? "" : "lg:order-1"}`}>
                    <div className="text-[11px] tracking-[0.3em] uppercase text-[#B8865B]/80 mb-4">
                      {s.tag}
                    </div>
                    <h3 className="font-display text-3xl md:text-4xl font-light tracking-tight text-[#F7F3EA] leading-[1.15]">
                      {s.title}
                    </h3>
                    <p className="mt-4 text-lg text-[#EDE4D3]/80 leading-snug italic">
                      {s.subtitle}
                    </p>
                    <div className="mt-6 h-px w-12 bg-[#B8865B]" />
                    <p className="mt-6 text-[#EDE4D3]/70 leading-relaxed font-light">
                      {s.description}
                    </p>
                    <ul className="mt-6 space-y-3">
                      {s.bullets.map((b) => (
                        <li
                          key={b}
                          className="flex items-start gap-3 text-sm text-[#EDE4D3]/85"
                        >
                          <Check
                            className="w-4 h-4 text-[#B8865B] mt-0.5 shrink-0"
                            strokeWidth={2}
                          />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 pt-6 border-t border-[#EDE4D3]/10">
                      <div>
                        <div className="text-[11px] tracking-[0.2em] uppercase text-[#EDE4D3]/50">
                          Engagement
                        </div>
                        <div className="mt-1 font-display text-2xl text-[#B8865B]">
                          {s.price}
                        </div>
                        <div className="text-xs text-[#EDE4D3]/50 mt-1">
                          {s.priceNote}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => openInquiry("__consultancy__")}
                        data-testid={`service-cta-${s.id}`}
                        className="inline-flex items-center justify-center gap-2 border border-[#B8865B] text-[#B8865B] hover:bg-[#B8865B] hover:text-[#1F2620] px-6 py-3 rounded-md text-sm font-medium tracking-wide transition-all"
                      >
                        Engage on this service
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-[#171C17] py-24 md:py-28 border-y border-[#EDE4D3]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#B8865B] mb-5">
              How we engage
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight text-[#F7F3EA] leading-[1.1]">
              Four steps.{" "}
              <span className="italic text-[#B8865B]">Written throughout</span>.
            </h2>
          </motion.div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {consultancyProcess.map((step, i) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                data-testid={`process-step-${step.number}`}
                className="relative bg-[#1F2620] border border-[#EDE4D3]/10 rounded-xl p-8"
              >
                <div className="font-display text-5xl text-[#B8865B]/30 leading-none">
                  {step.number}
                </div>
                <div className="font-display text-lg text-[#F7F3EA] mt-6">
                  {step.title}
                </div>
                <p className="mt-3 text-sm text-[#EDE4D3]/65 leading-relaxed">
                  {step.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT / CTA */}
      <section id="contact" className="py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
          >
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#B8865B] mb-6">
              Begin a Conversation
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight text-[#F7F3EA] leading-[1.1]">
              A quieter, better-run{" "}
              <span className="italic text-[#B8865B]">portfolio</span>.
            </h2>
            <p className="mt-6 text-[#EDE4D3]/70 leading-relaxed max-w-2xl mx-auto">
              Every introductory call is offered without fee or commitment. We
              respond to every enquiry within one business day.
            </p>
            <button
              type="button"
              onClick={() => openInquiry("__consultancy__")}
              data-testid="consultancy-cta-bottom"
              className="mt-10 inline-flex items-center justify-center gap-2 bg-[#B8865B] hover:bg-[#A57548] text-[#1F2620] px-10 py-4 rounded-md font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5 shadow-lg shadow-black/30"
            >
              Request Consultation
              <ArrowRight className="w-4 h-4" />
            </button>

            <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 text-left border-t border-[#EDE4D3]/10 pt-16">
              <div>
                <div className="text-[11px] tracking-[0.2em] uppercase text-[#B8865B]/80 mb-3">
                  Email
                </div>
                <a
                  href="mailto:info@kenneth-llc.com"
                  className="flex items-center gap-3 text-[#EDE4D3] hover:text-[#B8865B] transition-colors"
                >
                  <Mail className="w-4 h-4 text-[#B8865B]" />
                  info@kenneth-llc.com
                </a>
              </div>
              <div>
                <div className="text-[11px] tracking-[0.2em] uppercase text-[#B8865B]/80 mb-3">
                  Phone
                </div>
                <a
                  href="tel:+17622269357"
                  className="flex items-center gap-3 text-[#EDE4D3] hover:text-[#B8865B] transition-colors"
                >
                  <Phone className="w-4 h-4 text-[#B8865B]" />
                  +1 (762) 226-9357
                </a>
              </div>
              <div>
                <div className="text-[11px] tracking-[0.2em] uppercase text-[#B8865B]/80 mb-3">
                  Registered Office
                </div>
                <div className="flex items-start gap-3 text-[#EDE4D3]/80 text-sm leading-relaxed">
                  <MapPin className="w-4 h-4 text-[#B8865B] mt-0.5 shrink-0" />
                  <span>
                    30 N Gould St Ste R<br />
                    Sheridan, WY 82801, USA
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
